import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { useDocsExpirations, type DocsExpiration } from '@/api/docs';
import { StatCard } from '@/components/dashboard/StatCard';
import { Chip, Empty, ListPage, PageLoading, Th, tableWrap, theadCls, useListState, type FilterDef } from '@/components/network/NetworkKit';
import { ExpiryChip, daysUntil, kindLabel } from '@/components/docs/DocsKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { docsOrgPath } from '@/lib/routes';

/**
 * IT Glue expirations across every organisation — warranties, SSL certificates, domains, Azure
 * tokens, licences. Password expirations are never included (filtered server-side).
 */

const within = (e: DocsExpiration, lo: number, hi: number) => { const d = daysUntil(e.date); return d != null && d >= lo && d <= hi; };
const FILTERS: FilterDef<DocsExpiration>[] = [
  { id: 'soon',    label: 'Next 90 days',  test: (e) => within(e, 0, 90) },
  { id: '30',      label: 'Next 30 days',  test: (e) => within(e, 0, 30) },
  { id: 'year',    label: 'Next 12 months', test: (e) => within(e, 0, 365) },
  { id: 'expired', label: 'Expired (last 90 days)', test: (e) => within(e, -90, -1) },
  { id: 'all',     label: 'All',           test: () => true },
];

export default function DocsExpirationsPage() {
  const { data, isLoading, error, isFetching } = useDocsExpirations({ scoped: true });
  const [kind, setKind] = useState('');
  const all = data?.items ?? [];
  const items = useMemo(() => (kind ? all.filter((e) => e.kind === kind) : all), [all, kind]);
  const kinds = useMemo(() => [...new Set(all.map((e) => e.kind))].sort(), [all]);
  const l = useListState('cosmos:docs:expirations:show', FILTERS, items, (e) => `${e.name} ${e.orgName} ${e.companyName ?? ''} ${e.kind} ${e.description ?? ''}`);
  const rows = useMemo(() => [...l.rows].sort((a, b) => (a.date ?? '').localeCompare(b.date ?? '')), [l.rows]);
  const kindCount = (k: string) => all.filter((e) => e.kind === k && within(e, 0, 90)).length;

  return (
    <ListPage area="Docs" title="Expirations" count={data ? rows.length : null} isFetching={isFetching}
      subtitle={data && <>IT Glue · warranties, certificates, domains, tokens, licences · read {fmtRelative(data.fetchedAt)}</>}
      search={{ q: l.q, setQ: l.setQ, placeholder: 'Item, client or description' }}
      actions={
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground">
          <option value="">All kinds</option>
          {kinds.map((k) => <option key={k} value={k}>{kindLabel(k)}</option>)}
        </select>
      }
      filters={{ defs: FILTERS, show: l.show, pick: l.pick, counts: l.counts }}>
      <PageLoading isLoading={isLoading} error={error} vendor="IT Glue expirations" />
      {data && (
        <>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {[
              { k: 'SslCertificate', label: 'SSL certificates' },
              { k: 'Domain', label: 'Domains' },
              { k: 'Azure Token', label: 'Azure tokens' },
              { k: 'Workstation', label: 'Warranties' },
              { k: 'Licensing', label: 'Licensing' },
            ].filter((x) => kinds.includes(x.k)).map((x) => (
              <StatCard key={x.k} label={x.label} value={kindCount(x.k)} sub="expiring in 90 days" tone={kindCount(x.k) ? 'warning' : 'success'}
                onClick={() => { setKind(x.k); l.pick('soon'); }} />
            ))}
          </div>
          <div className={tableWrap}>
            <table className="w-full text-sm" style={{ minWidth: 960 }}>
              <thead className={theadCls}><tr><Th>Expires</Th><Th>Kind</Th><Th>Item</Th><Th>Client</Th><Th>Description</Th><Th /></tr></thead>
              <tbody className="divide-y divide-divider">
                {rows.length === 0 && <Empty cols={6} />}
                {rows.map((e) => (
                  <tr key={e.id} className="hover:bg-muted/30">
                    <td className="whitespace-nowrap px-3 py-2"><ExpiryChip date={e.date} /></td>
                    <td className="px-3 py-2"><Chip tone="muted">{kindLabel(e.kind)}</Chip></td>
                    <td className="max-w-[260px] truncate px-3 py-2 font-medium text-foreground" title={e.name}>{e.name || '—'}</td>
                    <td className="max-w-[240px] truncate px-3 py-2 text-xs"><Link to={docsOrgPath(e.orgId, 'expirations')} className="text-foreground hover:underline" title={e.orgName}>{e.companyName ?? e.orgName}</Link></td>
                    <td className="max-w-[360px] truncate px-3 py-2 text-xs text-muted-foreground" title={e.description ?? ''}>{e.description ?? '—'}</td>
                    <td className="px-3 py-2 text-right">{e.url && <a href={e.url} target="_blank" rel="noreferrer" title="Open in IT Glue" className="text-muted-foreground hover:text-foreground"><ExternalLink className="h-3.5 w-3.5" /></a>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </ListPage>
  );
}
